import { useEffect, useRef } from "react";
import {
  CHAT_VOICE_WAVE_BARS,
  chatVoiceLiveBarCount,
  drawVoiceWaveform,
  type ChatVoiceMeter,
} from "@/lib/chatVoice";

type Props = {
  meter: ChatVoiceMeter | null;
  paused?: boolean;
};

/** Onde qui défile pendant l’enregistrement (style WhatsApp). */
export function ChatVoiceLiveWave({ meter, paused = false }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const levelsRef = useRef<number[]>(Array.from({ length: CHAT_VOICE_WAVE_BARS }, () => 0.08));
  const lastPushRef = useRef(0);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !meter) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf = 0;
    const paint = (now: number) => {
      const want = chatVoiceLiveBarCount(canvas.clientWidth || 1);
      if (levelsRef.current.length !== want) {
        const prev = levelsRef.current;
        if (want > prev.length) {
          levelsRef.current = [
            ...Array.from({ length: want - prev.length }, () => 0.08),
            ...prev,
          ];
        } else {
          levelsRef.current = prev.slice(prev.length - want);
        }
      }
      if (!pausedRef.current && now - lastPushRef.current >= 55) {
        lastPushRef.current = now;
        const next = levelsRef.current.slice(1);
        next.push(0.07 + meter.readLevel() * 0.93);
        levelsRef.current = next;
      }
      drawVoiceWaveform(ctx, canvas, levelsRef.current);
      raf = window.requestAnimationFrame(paint);
    };

    raf = window.requestAnimationFrame(paint);
    return () => window.cancelAnimationFrame(raf);
  }, [meter]);

  return <canvas ref={canvasRef} className="chat-voice-live-wave" aria-hidden />;
}
